import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import pb from '../../../lib/pocketbase';
import { jsPDF } from "jspdf";
import autoTable from 'jspdf-autotable';
import '../Admin.css';

const Requests = () => {
    const navigate = useNavigate();
    const [search, setSearch] = useState('');
    const [filterStatus, setFilterStatus] = useState('all');
    const [requestsData, setRequestsData] = useState([]);
    const [companySettings, setCompanySettings] = useState(null);
    const [loading, setLoading] = useState(true);

    // Editing State
    const [editingRequest, setEditingRequest] = useState(null);
    const [formData, setFormData] = useState({});
    const [notifyClient, setNotifyClient] = useState(false);

    // Advanced Quote Editor State
    const [quoteLines, setQuoteLines] = useState([]);
    const [activeTab, setActiveTab] = useState('lines'); // lines, client, vehicle, conditions

    // Preview State
    const [previewRequest, setPreviewRequest] = useState(null);

    const fetchRequests = async () => {
        setLoading(true);
        try {
            const records = await pb.collection('quotes').getFullList({
                sort: '-created',
                expand: 'users',
            });
            setRequestsData(records);

            // Fetch Company Settings for PDF
            try {
                const settings = await pb.collection('company_settings').getList(1, 1);
                if (settings.items.length > 0) {
                    setCompanySettings(settings.items[0]);
                }
            } catch (e) { console.log("No settings"); }

        } catch (err) {
            console.error('Erreur fetch requests:', err);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchRequests();
    }, []);

    // --- ACTIONS ---

    const handleEditClick = (request) => {
        setEditingRequest(request);

        // Initialize simple fields
        const initialData = {
            status: request.status,
            total_price: request.total_price,
            notes: request.notes || '',
            // Client
            firstName: request.firstName,
            lastName: request.lastName,
            email: request.email,
            phone: request.phone,
            address: request.address || '',
            // Vehicle
            vehicle_brand: request.vehicle_brand,
            vehicle_model: request.vehicle_model,
            vehicle_plate: request.vehicle_plate,
            vehicle_vin: request.vehicle_vin || '',
            vehicle_mileage: request.vehicle_mileage || '',
            // Conditions
            validity_date: request.validity_date || new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
            deposit_amount: request.deposit_amount !== undefined ? request.deposit_amount : 0,
            vat_rate: request.vat_rate !== undefined ? request.vat_rate : 20,
            public_notes: request.public_notes || '',
        };
        setFormData(initialData);

        // Initialize Quote Lines
        let lines = [];
        if (request.quote_lines_json) {
            try {
                lines = JSON.parse(request.quote_lines_json);
            } catch (e) { lines = []; }
        }

        // Fallback: if no JSON lines, create default line from total price or services string
        if (lines.length === 0) {
            if (request.services_list) {
                // Try to split services and give them a share of price or just 0
                const serviceNames = request.services_list.split(',');
                lines = serviceNames.map((s, i) => ({
                    id: Date.now() + i,
                    description: s.trim(),
                    quantity: 1,
                    unit_price: i === 0 ? request.total_price : 0, // Simplified logic
                    discount: 0,
                    total: i === 0 ? request.total_price : 0
                }));
            } else {
                lines = [{
                    id: Date.now(),
                    description: 'Prestation principale',
                    quantity: 1,
                    unit_price: request.total_price,
                    discount: 0,
                    total: request.total_price
                }];
            }
        }
        setQuoteLines(lines);
        setNotifyClient(false);
        setActiveTab('lines');
    };

    const updateLine = (id, field, value) => {
        setQuoteLines(prev => prev.map(line => {
            if (line.id === id) {
                const updatedLine = { ...line, [field]: value };
                // Recalculate line total
                const qty = field === 'quantity' ? parseFloat(value) : line.quantity;
                const price = field === 'unit_price' ? parseFloat(value) : line.unit_price;
                const discount = field === 'discount' ? parseFloat(value) : line.discount;
                updatedLine.total = (qty * price) * (1 - (discount / 100));
                return updatedLine;
            }
            return line;
        }));
    };

    const addLine = () => {
        setQuoteLines([...quoteLines, {
            id: Date.now(),
            description: '',
            quantity: 1,
            unit_price: 0,
            discount: 0,
            total: 0
        }]);
    };

    const removeLine = (id) => {
        setQuoteLines(quoteLines.filter(l => l.id !== id));
    };

    // Auto-calculate global total when lines change
    useEffect(() => {
        if (!editingRequest) return;
        const newTotal = quoteLines.reduce((acc, line) => acc + (line.total || 0), 0);
        setFormData(prev => ({ ...prev, total_price: parseFloat(newTotal.toFixed(2)) }));
    }, [quoteLines, editingRequest]);

    const handleSaveRequest = async () => {
        if (!editingRequest) return;
        try {
            const dataToUpdate = {
                status: formData.status,
                total_price: parseFloat(formData.total_price),
                notes: formData.notes,
                // New Fields
                firstName: formData.firstName,
                lastName: formData.lastName,
                email: formData.email,
                phone: formData.phone,
                address: formData.address,
                vehicle_brand: formData.vehicle_brand,
                vehicle_model: formData.vehicle_model,
                vehicle_plate: formData.vehicle_plate,
                vehicle_vin: formData.vehicle_vin,
                vehicle_mileage: formData.vehicle_mileage,
                validity_date: formData.validity_date,
                deposit_amount: formData.deposit_amount,
                vat_rate: formData.vat_rate,
                public_notes: formData.public_notes,
                // JSON lines
                quote_lines_json: JSON.stringify(quoteLines),
                // Update simple string list for backward compatibility
                services_list: quoteLines.map(l => l.description).join(', ')
            };

            await pb.collection('quotes').update(editingRequest.id, dataToUpdate);

            if (notifyClient) {
                alert(`📧 Email envoyé à ${formData.email} :\n"Bonjour ${formData.firstName}, le statut de votre devis est maintenant : ${statusLabelMapNames[formData.status]}."`);
            }

            setEditingRequest(null);
            fetchRequests();
        } catch (err) {
            console.error(err);
            alert('Note: Certains champs étendus (adresse, VIN, JSON lines) peuvent ne pas être sauvegardés si le schéma PocketBase n\'est pas à jour. Le prix total et le statut ont été mis à jour.');
            // Fallback save for core fields if Schema forbids extra
            try {
                await pb.collection('quotes').update(editingRequest.id, {
                    status: formData.status,
                    total_price: parseFloat(formData.total_price),
                    notes: formData.notes
                });
                setEditingRequest(null);
                fetchRequests();
            } catch (e) { alert('Erreur critique de sauvegarde.'); }
        }
    };

    const handleCreateInvoice = async () => {
        if (!editingRequest) return;

        const confirm = window.confirm(`Générer une facture de ${formData.total_price} € pour ${formData.firstName} ${formData.lastName} ?`);
        if (!confirm) return;

        try {
            const invoiceData = {
                users: editingRequest.users || editingRequest.expand?.users?.id,
                quote: editingRequest.id,
                amount: parseFloat(formData.total_price),
                status: 'pending',
                invoice_number: `FAC-${new Date().getFullYear()}-${Math.floor(1000 + Math.random() * 9000)}`
            };

            if (!invoiceData.users && editingRequest.user_id) invoiceData.users = editingRequest.user_id;

            await pb.collection('invoices').create(invoiceData);
            alert('✅ Facture générée avec succès ! Retrouvez-la dans l\'onglet Facturation.');
            setEditingRequest(null);
        } catch (err) {
            console.error(err);
            alert('Erreur lors de la création de la facture.');
        }
    };

    const handleQuickUpdate = async (id, newStatus) => {
        try {
            await pb.collection('quotes').update(id, { status: newStatus });
            fetchRequests(); // Refresh list
        } catch (err) {
            console.error('Erreur lors de la mise à jour rapide:', err);
            alert('Erreur lors de la mise à jour du statut.');
        }
    };

    const handleDelete = async (id) => {
        if (!window.confirm('Êtes-vous sûr de vouloir supprimer ce devis ? Cette action est irréversible.')) return;
        try {
            await pb.collection('quotes').delete(id);
            fetchRequests();
        } catch (err) {
            console.error(err);
            alert('Erreur lors de la suppression');
        }
    };

    const handleShare = (req) => {
        const subject = `Votre Devis ${req.id.substring(0, 8).toUpperCase()}`;
        const body = `Bonjour ${req.firstName || 'Client'},\n\nVeuillez trouver ci-joint votre devis.\n\nCordialement,\n${companySettings?.company_name || "L'Univers Diag"}`;
        const mailtoLink = `mailto:${req.email}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
        window.open(mailtoLink, '_blank');
    };

    const generatePDF = (quote) => {
        // Use either preview quote data or form data if editing
        const data = quote || editingRequest;
        // If we are previewing a raw quote from list, we need to parse lines if available
        let lines = [];
        if (data.quote_lines_json) {
            try { lines = JSON.parse(data.quote_lines_json); } catch (e) { }
        }
        // Fallback lines
        if (lines.length === 0) {
            if (data.services_list) {
                const sList = data.services_list.split(',');
                lines = sList.map((s, i) => ({ description: s, total: i === 0 ? data.total_price : 0, quantity: 1, unit_price: 0 }));
            } else {
                lines = [{ description: 'Prestation', total: data.total_price, quantity: 1, unit_price: data.total_price }];
            }
        }

        const doc = new jsPDF();
        const pageWidth = doc.internal.pageSize.getWidth();
        const marginLeft = 20;

        const companyName = companySettings?.company_name || "L'Univers Diag";
        const companyInfo = `${companySettings?.address || 'Toulouse'} - ${companySettings?.phone || ''}`;

        // Header
        doc.setFontSize(22);
        doc.setTextColor(37, 99, 235);
        doc.text(companyName, marginLeft, 20);

        doc.setFontSize(10);
        doc.setTextColor(100);
        doc.text(companyInfo, marginLeft, 26);
        doc.text(companySettings?.email || "contact@luniversdiag.fr", marginLeft, 31);

        // Document Info
        doc.setFontSize(16);
        doc.setTextColor(0);
        doc.text("DEVIS", pageWidth - marginLeft, 25, { align: 'right' });
        doc.setFontSize(10);
        doc.setTextColor(100);
        doc.text(`Réf: ${data.id.substring(0, 8).toUpperCase()}`, pageWidth - marginLeft, 32, { align: 'right' });
        doc.text(`Date: ${new Date(data.created).toLocaleDateString()}`, pageWidth - marginLeft, 37, { align: 'right' });
        if (data.validity_date) {
            doc.text(`Valable jusqu'au: ${new Date(data.validity_date).toLocaleDateString()}`, pageWidth - marginLeft, 42, { align: 'right' });
        }

        // Client & Vehicle Grid
        const startY = 55;
        doc.setDrawColor(226, 232, 240);
        doc.line(marginLeft, 48, pageWidth - marginLeft, 48);

        // Client Column
        doc.setFontSize(10);
        doc.setTextColor(148, 163, 184); // Slate 400
        doc.text("DESTINATAIRE", marginLeft, startY);

        doc.setTextColor(15, 23, 42); // Slate 900
        doc.setFont("helvetica", "bold");
        doc.text(`${data.firstName || ''} ${data.lastName || ''}`, marginLeft, startY + 6);
        doc.setFont("helvetica", "normal");
        doc.setTextColor(71, 85, 105);
        if (data.address) doc.text(data.address, marginLeft, startY + 11);
        doc.text(data.email || '', marginLeft, startY + 16);
        if (data.phone) doc.text(data.phone, marginLeft, startY + 21);

        // Vehicle Column
        const col2X = pageWidth / 2 + 10;
        doc.setFont("helvetica", "normal");
        doc.setTextColor(148, 163, 184);
        doc.text("VÉHICULE", col2X, startY);

        doc.setTextColor(15, 23, 42);
        doc.setFont("helvetica", "bold");
        doc.text(`${data.vehicle_brand || ''} ${data.vehicle_model || ''}`, col2X, startY + 6);
        doc.setFont("helvetica", "normal");
        doc.setTextColor(71, 85, 105);
        doc.text(`Immat: ${data.vehicle_plate || 'Non renseignée'}`, col2X, startY + 11);
        if (data.vehicle_vin) doc.text(`VIN: ${data.vehicle_vin}`, col2X, startY + 16);
        if (data.vehicle_mileage) doc.text(`Km: ${data.vehicle_mileage}`, col2X, startY + 21);

        // Services Table
        const tableBody = lines.map(line => [
            line.description,
            line.quantity,
            `${parseFloat(line.unit_price).toFixed(2)} €`,
            line.discount ? `${line.discount}%` : '-',
            `${parseFloat(line.total).toFixed(2)} €`
        ]);

        autoTable(doc, {
            startY: startY + 30,
            head: [['Description', 'Qté', 'Prix Unit.', 'Remise', 'Total HT']],
            body: tableBody,
            theme: 'plain',
            headStyles: {
                fillColor: [248, 250, 252],
                textColor: [100, 116, 139],
                fontStyle: 'bold',
                lineWidth: { bottom: 0.5 },
                lineColor: [226, 232, 240]
            },
            styles: {
                cellPadding: 4,
                fontSize: 9,
                textColor: [51, 65, 85],
                lineWidth: { bottom: 0.5 },
                lineColor: [241, 245, 249]
            },
            columnStyles: {
                0: { cellWidth: 'auto' }, // Description
                4: { halign: 'right', fontStyle: 'bold' } // Total
            },
        });

        // Totals
        const finalY = doc.lastAutoTable.finalY + 10;
        const rightColX = pageWidth - marginLeft - 40;
        const amountX = pageWidth - marginLeft;

        // Public Note
        if (data.public_notes) {
            doc.setFontSize(9);
            doc.setTextColor(100);
            doc.text("Note:", marginLeft, finalY);
            doc.setTextColor(50);
            const splitNote = doc.splitTextToSize(data.public_notes, 100);
            doc.text(splitNote, marginLeft, finalY + 5);
        }

        doc.setFontSize(10);
        doc.setTextColor(100);
        doc.text("Total HT", rightColX, finalY, { align: 'right' });
        doc.setTextColor(0);
        doc.text(`${parseFloat(data.total_price).toFixed(2)} €`, amountX, finalY, { align: 'right' });

        if (data.vat_rate > 0) {
            const vatAmount = (data.total_price * data.vat_rate / 100).toFixed(2);
            doc.setTextColor(100);
            doc.text(`TVA (${data.vat_rate}%)`, rightColX, finalY + 6, { align: 'right' });
            doc.setTextColor(0);
            doc.text(`${vatAmount} €`, amountX, finalY + 6, { align: 'right' });

            doc.setFontSize(12);
            doc.setFont("helvetica", "bold");
            doc.text("Total TTC", rightColX, finalY + 14, { align: 'right' });
            doc.setTextColor(37, 99, 235);
            const totalTTC = (parseFloat(data.total_price) + parseFloat(vatAmount)).toFixed(2);
            doc.text(`${totalTTC} €`, amountX, finalY + 14, { align: 'right' });
        } else {
            doc.setFontSize(12);
            doc.setFont("helvetica", "bold");
            doc.text("Total à payer", rightColX, finalY + 10, { align: 'right' });
            doc.setTextColor(37, 99, 235);
            doc.text(`${parseFloat(data.total_price).toFixed(2)} €`, amountX, finalY + 10, { align: 'right' });

            doc.setFontSize(8);
            doc.setFont("helvetica", "normal");
            doc.setTextColor(150);
            doc.text("TVA non applicable, art. 293 B du CGI", amountX, finalY + 16, { align: 'right' });
        }

        doc.save(`Devis_${data.id.substring(0, 6)}.pdf`);
    };

    // --- MAPPINGS ---
    const statusBadgeMap = {
        en_attente: 'pending',
        valide: 'progress',
        diagnostic: 'progress',
        installation: 'progress',
        test: 'progress',
        pret: 'completed',
        termine: 'completed',
        refuse: 'cancelled'
    };

    const statusLabelMapNames = {
        en_attente: 'En attente',
        valide: 'Validé',
        diagnostic: 'Diag en cours',
        installation: 'Installation',
        test: 'Tests',
        pret: 'Prêt',
        termine: 'Terminé',
        refuse: 'Refusé'
    };

    const filteredData = requestsData.filter(req => {
        const matchesSearch = (req.firstName + ' ' + req.lastName).toLowerCase().includes(search.toLowerCase()) ||
            req.vehicle_plate.toLowerCase().includes(search.toLowerCase());
        const matchesStatus = filterStatus === 'all' || statusBadgeMap[req.status] === filterStatus;
        return matchesSearch && matchesStatus;
    });

    if (loading) return <div className="admin-content-wrapper">Chargement...</div>;

    return (
        <div className="admin-content-wrapper">
            <section className="intro-card admin-intro">
                <div className="admin-header-content">
                    <h2>Demandes & Devis</h2>
                    <p>Gérez les demandes clients et transformez-les en factures.</p>
                </div>
            </section>

            <section className="card full-width-card">
                <div className="filter-bar">
                    <div className="search-input-wrapper">
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="8"></circle><line x1="21" y1="21" x2="16.65" y2="16.65"></line></svg>
                        <input type="text" placeholder="Search..." value={search} onChange={(e) => setSearch(e.target.value)} />
                    </div>
                </div>

                <div className="table-responsive">
                    <table className="data-table admin-table">
                        <thead>
                            <tr>
                                <th>Client</th>
                                <th>Véhicule</th>
                                <th>Services</th>
                                <th>Prix Total (HT/TTC)</th>
                                <th>Statut</th>
                                <th>Notes</th>
                                <th className="text-right">Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {filteredData.map((req) => (
                                <tr key={req.id}>
                                    <td>
                                        <div className="user-cell">
                                            <div className="user-avatar-small">{req.firstName?.[0]}</div>
                                            <div className="user-info">
                                                <span className="user-name">{req.firstName} {req.lastName}</span>
                                                <span className="user-sub">{req.email}</span>
                                            </div>
                                        </div>
                                    </td>
                                    <td>
                                        <div><strong>{req.vehicle_brand}</strong> {req.vehicle_model}</div>
                                        <div className="id-cell">{req.vehicle_plate}</div>
                                    </td>
                                    <td><span style={{ fontSize: '0.85rem' }}>{req.services_list ? req.services_list.substring(0, 30) + (req.services_list.length > 30 ? '...' : '') : 'Divers'}</span></td>
                                    <td><strong>{req.total_price} €</strong></td>
                                    <td><span className={`status-badge ${statusBadgeMap[req.status]}`}>{statusLabelMapNames[req.status]}</span></td>
                                    <td style={{ fontSize: '0.8rem', color: '#94a3b8', maxWidth: '150px' }} className="truncate">{req.notes || '-'}</td>
                                    <td className="text-right">
                                        <div className="action-buttons">
                                            {/* Quick Actions */}
                                            {req.status === 'en_attente' && (
                                                <>
                                                    <button
                                                        className="icon-btn"
                                                        title="Valider la demande"
                                                        onClick={() => handleQuickUpdate(req.id, 'valide')}
                                                        style={{ color: '#22c55e', background: 'rgba(34, 197, 94, 0.1)', padding: '4px', borderRadius: '4px' }}
                                                    >
                                                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"></polyline></svg>
                                                    </button>
                                                    <button
                                                        className="icon-btn"
                                                        title="Rejeter la demande"
                                                        onClick={() => handleQuickUpdate(req.id, 'refuse')}
                                                        style={{ color: '#ef4444', background: 'rgba(239, 68, 68, 0.1)', padding: '4px', borderRadius: '4px' }}
                                                    >
                                                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>
                                                    </button>
                                                </>
                                            )}

                                            {/* Prop 1: Global Cancel for active items */}
                                            {['valide', 'diagnostic', 'installation', 'test', 'pret'].includes(req.status) && (
                                                <button
                                                    className="icon-btn"
                                                    title="Annuler l'intervention"
                                                    onClick={() => handleQuickUpdate(req.id, 'refuse')}
                                                    style={{ color: '#f97316', background: 'rgba(249, 115, 22, 0.1)', padding: '4px', borderRadius: '4px' }}
                                                >
                                                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"></circle><line x1="4.93" y1="4.93" x2="19.07" y2="19.07"></line></svg>
                                                </button>
                                            )}

                                            {/* Planifier RDV (Prop 2) */}
                                            {req.status === 'valide' && (
                                                <button
                                                    className="icon-btn"
                                                    title="Planifier le rendez-vous"
                                                    onClick={() => navigate('/admin/planning', { state: { planQuote: req } })}
                                                    style={{ color: '#facc15', background: 'rgba(250, 204, 21, 0.1)', padding: '4px', borderRadius: '4px' }}
                                                >
                                                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect><line x1="16" y1="2" x2="16" y2="6"></line><line x1="8" y1="2" x2="8" y2="6"></line><line x1="3" y1="10" x2="21" y2="10"></line></svg>
                                                </button>
                                            )}

                                            {/* Download PDF */}
                                            <button
                                                className="icon-btn"
                                                title="Télécharger PDF"
                                                onClick={() => generatePDF(req)}
                                                style={{ color: '#64748b' }}
                                            >
                                                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path><polyline points="7 10 12 15 17 10"></polyline><line x1="12" y1="15" x2="12" y2="3"></line></svg>
                                            </button>

                                            {/* Share / Email */}
                                            <button
                                                className="icon-btn"
                                                title="Envoyer par email"
                                                onClick={() => handleShare(req)}
                                                style={{ color: '#3b82f6' }}
                                            >
                                                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M4 12v8a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-8"></path><polyline points="16 6 12 2 8 6"></polyline><line x1="12" y1="2" x2="12" y2="15"></line></svg>
                                            </button>

                                            {/* Preview (Eye) */}
                                            <button
                                                className="icon-btn"
                                                title="Prévisualiser"
                                                onClick={() => setPreviewRequest(req)}
                                                style={{ color: '#8b5cf6' }} // Purple for view
                                            >
                                                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path><circle cx="12" cy="12" r="3"></circle></svg>
                                            </button>

                                            {/* Edit (Pencil) */}
                                            <button
                                                className="icon-btn"
                                                title="Modifier"
                                                onClick={() => handleEditClick(req)}
                                                style={{ color: '#3b82f6' }}
                                            >
                                                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path></svg>
                                            </button>

                                            {/* Delete */}
                                            <button
                                                className="icon-btn"
                                                title="Supprimer"
                                                onClick={() => handleDelete(req.id)}
                                                style={{ color: '#ef4444' }}
                                            >
                                                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="3 6 5 6 21 6"></polyline><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path><line x1="10" y1="11" x2="10" y2="17"></line><line x1="14" y1="11" x2="14" y2="17"></line></svg>
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </section>

            {/* ADVANCED EDIT MODAL PORTAL */}
            {editingRequest && createPortal(
                <div className="modal-backdrop" onClick={() => setEditingRequest(null)} style={{ zIndex: 99999 }}>
                    <div className="modal-content admin-modal-large" onClick={e => e.stopPropagation()} style={{ maxWidth: '800px', display: 'flex', flexDirection: 'column', maxHeight: '90vh' }}>

                        {/* Header */}
                        <div className="admin-modal-header">
                            <h2>Éditeur de Devis Avancé</h2>
                            <button className="modal-close" onClick={() => setEditingRequest(null)}>×</button>
                        </div>

                        {/* Tabs */}
                        <div style={{ display: 'flex', borderBottom: '1px solid rgba(148, 163, 184, 0.1)', background: 'rgba(15, 23, 42, 0.3)' }}>
                            {['lines', 'client', 'vehicle', 'conditions'].map(tab => (
                                <button
                                    key={tab}
                                    onClick={() => setActiveTab(tab)}
                                    style={{
                                        padding: '1rem 1.5rem',
                                        background: activeTab === tab ? 'rgba(59, 130, 246, 0.1)' : 'transparent',
                                        border: 'none',
                                        borderBottom: activeTab === tab ? '2px solid #3b82f6' : '2px solid transparent',
                                        color: activeTab === tab ? '#3b82f6' : '#94a3b8',
                                        fontWeight: activeTab === tab ? 'bold' : 'normal',
                                        cursor: 'pointer',
                                        flex: 1
                                    }}
                                >
                                    {tab === 'lines' && 'Prestations'}
                                    {tab === 'client' && 'Client'}
                                    {tab === 'vehicle' && 'Véhicule'}
                                    {tab === 'conditions' && 'Conditions & Notes'}
                                </button>
                            ))}
                        </div>

                        {/* Body - Scrollable */}
                        <div className="admin-modal-body" style={{ flex: 1, overflowY: 'auto', padding: '1.5rem' }}>

                            {/* TAB: PRESTATIONS */}
                            {activeTab === 'lines' && (
                                <div>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '1rem' }}>
                                        <div className="admin-modal-section-title" style={{ marginBottom: 0 }}>Lignes du Devis</div>
                                        <button className="secondary-button" onClick={addLine} style={{ fontSize: '0.8rem', padding: '0.4rem 0.8rem' }}>+ Ajouter Ligne</button>
                                    </div>

                                    <div style={{ display: 'grid', gridTemplateColumns: 'minmax(200px, 3fr) 80px 100px 80px 100px 40px', gap: '0.5rem', alignItems: 'center', marginBottom: '0.5rem', fontSize: '0.75rem', textTransform: 'uppercase', color: '#64748b', fontWeight: 'bold' }}>
                                        <div>Description</div>
                                        <div>Qté</div>
                                        <div>Prix Unit.</div>
                                        <div>Remise %</div>
                                        <div style={{ textAlign: 'right' }}>Total</div>
                                        <div></div>
                                    </div>

                                    {quoteLines.map((line, index) => (
                                        <div key={line.id} style={{ display: 'grid', gridTemplateColumns: 'minmax(200px, 3fr) 80px 100px 80px 100px 40px', gap: '0.5rem', marginBottom: '0.5rem', alignItems: 'center' }}>
                                            <input className="admin-input" value={line.description} onChange={(e) => updateLine(line.id, 'description', e.target.value)} placeholder="Description" />
                                            <input className="admin-input" type="number" value={line.quantity} onChange={(e) => updateLine(line.id, 'quantity', e.target.value)} />
                                            <input className="admin-input" type="number" value={line.unit_price} onChange={(e) => updateLine(line.id, 'unit_price', e.target.value)} />
                                            <input className="admin-input" type="number" value={line.discount} onChange={(e) => updateLine(line.id, 'discount', e.target.value)} />
                                            <div style={{ textAlign: 'right', fontWeight: 'bold' }}>{parseFloat(line.total).toFixed(2)} €</div>
                                            <button onClick={() => removeLine(line.id)} style={{ color: '#ef4444', background: 'transparent', border: 'none', cursor: 'pointer' }}>×</button>
                                        </div>
                                    ))}

                                    <div style={{ marginTop: '2rem', display: 'flex', justifyContent: 'flex-end', gap: '2rem', borderTop: '1px solid rgba(255,255,255,0.1)', paddingTop: '1rem' }}>
                                        <div style={{ textAlign: 'right' }}>
                                            <div style={{ fontSize: '0.9rem', color: '#94a3b8' }}>Total HT</div>
                                            <div style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#f8fafc' }}>{formData.total_price} €</div>
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* TAB: CLIENT */}
                            {activeTab === 'client' && (
                                <div className="pricing-input-grid">
                                    <div className="form-group">
                                        <label>Prénom</label>
                                        <input className="admin-input" value={formData.firstName} onChange={(e) => setFormData({ ...formData, firstName: e.target.value })} />
                                    </div>
                                    <div className="form-group">
                                        <label>Nom</label>
                                        <input className="admin-input" value={formData.lastName} onChange={(e) => setFormData({ ...formData, lastName: e.target.value })} />
                                    </div>
                                    <div className="form-group" style={{ gridColumn: 'span 2' }}>
                                        <label>Adresse Complète (Facturation)</label>
                                        <input className="admin-input" value={formData.address} onChange={(e) => setFormData({ ...formData, address: e.target.value })} placeholder="123 Rue de l'Exemple, 31000 Toulouse" />
                                    </div>
                                    <div className="form-group">
                                        <label>Email</label>
                                        <input className="admin-input" value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })} />
                                    </div>
                                    <div className="form-group">
                                        <label>Téléphone</label>
                                        <input className="admin-input" value={formData.phone} onChange={(e) => setFormData({ ...formData, phone: e.target.value })} />
                                    </div>
                                </div>
                            )}

                            {/* TAB: VEHICLE */}
                            {activeTab === 'vehicle' && (
                                <div className="pricing-input-grid">
                                    <div className="form-group">
                                        <label>Immatriculation</label>
                                        <input className="admin-input" value={formData.vehicle_plate} onChange={(e) => setFormData({ ...formData, vehicle_plate: e.target.value })} style={{ textTransform: 'uppercase', letterSpacing: '2px', fontWeight: 'bold' }} />
                                    </div>
                                    <div className="form-group">
                                        <label>Kilométrage (km)</label>
                                        <input className="admin-input" type="number" value={formData.vehicle_mileage} onChange={(e) => setFormData({ ...formData, vehicle_mileage: e.target.value })} />
                                    </div>
                                    <div className="form-group">
                                        <label>Marque</label>
                                        <input className="admin-input" value={formData.vehicle_brand} onChange={(e) => setFormData({ ...formData, vehicle_brand: e.target.value })} />
                                    </div>
                                    <div className="form-group">
                                        <label>Modèle</label>
                                        <input className="admin-input" value={formData.vehicle_model} onChange={(e) => setFormData({ ...formData, vehicle_model: e.target.value })} />
                                    </div>
                                    <div className="form-group" style={{ gridColumn: 'span 2' }}>
                                        <label>Numéro de série (VIN)</label>
                                        <input className="admin-input" value={formData.vehicle_vin} onChange={(e) => setFormData({ ...formData, vehicle_vin: e.target.value })} maxLength={17} style={{ fontFamily: 'monospace' }} />
                                    </div>
                                </div>
                            )}

                            {/* TAB: CONDITIONS & NOTES */}
                            {activeTab === 'conditions' && (
                                <div>
                                    <div className="pricing-input-grid" style={{ marginBottom: '2rem' }}>
                                        <div className="form-group">
                                            <label>Statut du Devis</label>
                                            <select
                                                value={formData.status}
                                                onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                                                className="admin-input"
                                            >
                                                {Object.entries(statusLabelMapNames).map(([key, label]) => (
                                                    <option key={key} value={key}>{label}</option>
                                                ))}
                                            </select>
                                        </div>
                                        <div className="form-group">
                                            <label>Date de Validité</label>
                                            <input type="date" className="admin-input" value={formData.validity_date ? new Date(formData.validity_date).toISOString().split('T')[0] : ''} onChange={(e) => setFormData({ ...formData, validity_date: e.target.value })} />
                                        </div>
                                        <div className="form-group">
                                            <label>Acompte demandé (€)</label>
                                            <input type="number" className="admin-input" value={formData.deposit_amount} onChange={(e) => setFormData({ ...formData, deposit_amount: e.target.value })} />
                                        </div>
                                        <div className="form-group">
                                            <label>Taux TVA (%)</label>
                                            <input type="number" className="admin-input" value={formData.vat_rate} onChange={(e) => setFormData({ ...formData, vat_rate: e.target.value })} placeholder="0 pour auto-entrepreneur" />
                                            <span style={{ fontSize: '0.7rem', color: '#64748b' }}>Mettre 0 si non assujetti</span>
                                        </div>
                                    </div>

                                    <div className="form-group" style={{ marginBottom: '1rem' }}>
                                        <label>Notes Publiques (Visibles sur le PDF)</label>
                                        <textarea
                                            value={formData.public_notes}
                                            onChange={(e) => setFormData({ ...formData, public_notes: e.target.value })}
                                            className="admin-input"
                                            rows="2"
                                            placeholder="Ex: Véhicule de prêt inclus, Acompte de 30% reçu..."
                                        />
                                    </div>

                                    <div className="form-group">
                                        <label>Notes Internes (Privé Admin)</label>
                                        <textarea
                                            value={formData.notes}
                                            onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                                            className="admin-input"
                                            rows="2"
                                            placeholder="Infos atelier, code clé..."
                                        />
                                    </div>

                                    <div className="form-group checkbox-group" style={{ marginTop: '1rem' }}>
                                        <input
                                            type="checkbox"
                                            id="notifyClient"
                                            checked={notifyClient}
                                            onChange={(e) => setNotifyClient(e.target.checked)}
                                        />
                                        <label htmlFor="notifyClient">Envoyer un email de notification au client</label>
                                    </div>
                                </div>
                            )}

                        </div>

                        {/* Footer */}
                        <div className="admin-modal-footer">
                            <button
                                className="secondary-button"
                                style={{ marginRight: 'auto', background: 'rgba(16, 185, 129, 0.1)', color: '#34d399', border: '1px solid rgba(16, 185, 129, 0.2)' }}
                                onClick={handleCreateInvoice}
                            >
                                Convertir en Facture
                            </button>
                            <button className="secondary-button" onClick={() => setEditingRequest(null)}>Fermer</button>
                            <button className="primary-button" onClick={handleSaveRequest}>Enregistrer les modifications</button>
                        </div>
                    </div>
                </div>,
                document.body
            )}

            {/* KEEP PREVIEW MODAL AS IS but use enhanced PDF generation */}
            {previewRequest && createPortal(
                <div className="modal-backdrop" onClick={() => setPreviewRequest(null)} style={{ zIndex: 99999 }}>
                    <div className="modal-content" onClick={e => e.stopPropagation()} style={{ maxWidth: '600px', padding: 0 }}>
                        <div className="modal-header-flex" style={{ padding: '1.5rem', borderBottom: '1px solid rgba(255,255,255,0.1)' }}>
                            <h3>Aperçu Client</h3>
                            <button className="modal-close" onClick={() => setPreviewRequest(null)}>×</button>
                        </div>
                        <div style={{ padding: '2rem', maxHeight: '70vh', overflowY: 'auto' }}>
                            {/* Reusing simplified preview layout */}
                            <div className="preview-header" style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '1.5rem' }}>
                                <div>
                                    <h4 style={{ margin: 0, color: '#3b82f6' }}>{companySettings?.company_name || 'Votre Garage'}</h4>
                                    <p style={{ fontSize: '0.85rem', color: '#94a3b8' }}>{new Date(previewRequest.created).toLocaleDateString()}</p>
                                </div>
                                <div style={{ textAlign: 'right' }}>
                                    <div style={{ fontWeight: 'bold' }}>DEVIS</div>
                                    <div style={{ fontSize: '0.85rem', color: '#94a3b8' }}>{previewRequest.id.substring(0, 8).toUpperCase()}</div>
                                </div>
                            </div>

                            <div style={{ background: '#1e293b', padding: '1rem', borderRadius: '0.5rem', marginBottom: '1.5rem' }}>
                                <strong style={{ display: 'block', marginBottom: '0.5rem', color: '#94a3b8' }}>CLIENT</strong>
                                <div>{previewRequest.firstName} {previewRequest.lastName}</div>
                                <div style={{ fontSize: '0.9rem', color: '#cbd5e1' }}>{previewRequest.email}</div>
                                <div style={{ fontSize: '0.9rem', color: '#cbd5e1' }}>{previewRequest.phone}</div>
                            </div>

                            <div style={{ marginBottom: '2rem' }}>
                                <h5 style={{ borderBottom: '1px solid #334155', paddingBottom: '0.5rem', marginBottom: '1rem' }}>Prestations</h5>
                                {/* Preview logic simplified - fetching from existing record */}
                                {previewRequest.quote_lines_json ? (
                                    JSON.parse(previewRequest.quote_lines_json).map((line, i) => (
                                        <div key={i} style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
                                            <span>{line.description} {line.quantity > 1 ? `(x${line.quantity})` : ''}</span>
                                            <span style={{ color: '#facc15' }}>{parseFloat(line.total).toFixed(2)} €</span>
                                        </div>
                                    ))
                                ) : previewRequest.services_list ? (
                                    previewRequest.services_list.split(',').map((s, i) => (
                                        <div key={i} style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
                                            <span>{s}</span>
                                            <span style={{ color: '#64748b' }}>-</span>
                                        </div>
                                    ))
                                ) : (
                                    <div>Prestations standards</div>
                                )}
                                <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '1rem', paddingTop: '1rem', borderTop: '1px solid #334155', fontWeight: 'bold', fontSize: '1.2rem' }}>
                                    <span>Total</span>
                                    <span style={{ color: '#facc15' }}>{previewRequest.total_price} €</span>
                                </div>
                            </div>

                            <div style={{ display: 'flex', gap: '1rem' }}>
                                <button className="primary-button full-width" onClick={() => generatePDF(previewRequest)}>
                                    Télécharger PDF
                                </button>
                                <button className="secondary-button full-width" onClick={() => setPreviewRequest(null)}>
                                    Fermer
                                </button>
                            </div>
                        </div>
                    </div>
                </div>,
                document.body
            )}
        </div>
    );
};

export default Requests;
